import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";

/**
 * Page transitions inspired by the Kiwami logo's triangle apex.
 *
 * • Outgoing page: short fade out + tiny upward shift (≈200ms)
 * • A thin diagonal gold sliver wipes vertically once, mimicking
 *   the slanted edges of the logo apex (≈500ms)
 * • Incoming page: fade-up (≈400ms)
 *
 * Respects prefers-reduced-motion.
 */
const easing = [0.76, 0, 0.24, 1];

const pageVariants = {
  initial: { opacity: 0, y: 16 },
  in: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: easing, delay: 0.1 },
  },
  out: {
    opacity: 0,
    y: -8,
    transition: { duration: 0.22, ease: easing },
  },
};

const PageTransition = ({ children }) => {
  const location = useLocation();

  // prefers-reduced-motion — derived, never stored
  const reduced = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  if (reduced) {
    return <>{children}</>;
  }

  return (
    <>
      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          variants={pageVariants}
          initial="initial"
          animate="in"
          exit="out"
        >
          {children}
        </motion.div>
      </AnimatePresence>

      {/* Diagonal gold sliver — keyed to pathname so it remounts each navigation */}
      <motion.div
        key={`slash-${location.pathname}`}
        aria-hidden
        initial={{ scaleY: 0, transformOrigin: "top center", opacity: 0 }}
        animate={{
          scaleY: [0, 1, 1, 0],
          transformOrigin: [
            "top center",
            "top center",
            "bottom center",
            "bottom center",
          ],
          opacity: [0, 0.85, 0.85, 0],
        }}
        transition={{
          duration: 0.7,
          times: [0, 0.35, 0.55, 1],
          ease: easing,
        }}
        className="fixed inset-0 z-[60] pointer-events-none"
        style={{
          background:
            "linear-gradient(108deg, transparent 0%, transparent 49.4%, rgba(197,160,89,0.9) 49.9%, #C5A059 50%, rgba(197,160,89,0.9) 50.1%, transparent 50.6%, transparent 100%)",
        }}
      />
    </>
  );
};

export default PageTransition;
